// src/components/pages/admin-dash/AdminRateLimit.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Button from "../../ui/Button";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

/**
 * Live snapshot of RequestRateLimitFilter's in-memory state
 * (RateLimitAdminResource, /admin/rate-limit/snapshot) — who/what is
 * currently within a rate-limit window, and how close to the limit.
 * In-memory only, so this only shows activity since the app last
 * restarted, and only on this instance. 3-POS_FASE5_ROADMAP.md item 4.10.
 */
function statusBadge(count, limit) {
  if (limit > 0 && count >= limit) return <Badge variant="red">Blocked</Badge>;
  if (limit > 0 && count / limit >= 0.8) return <Badge variant="gold">Near limit</Badge>;
  return <Badge variant="neutral">OK</Badge>;
}

// Parses "user:42:POST:/admin/user/bulk-credit" into who/method/path —
// the filter's key format (see RequestRateLimitFilter#buildKey).
function parseKey(key) {
  const parts = String(key || "").split(":");
  const who = parts.length >= 2 ? `${parts[0]}:${parts[1]}` : key;
  const rest = parts.slice(2).join(":");
  const [method, ...pathParts] = rest.split(":");
  return { who, method: method || "", path: pathParts.join(":") || rest };
}

export default function AdminRateLimit({ onShowToast }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSnapshot = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/rate-limit/snapshot");
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch {
      onShowToast?.("Couldn't load rate-limit snapshot.", "error");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSnapshot(); }, []); // eslint-disable-line

  const columns = [
    { key: "who", label: "Who", sort: true, value: (e) => parseKey(e.key).who,
      render: (e) => <span className="font-medium" style={{ color: "var(--txt)" }}>{parseKey(e.key).who}</span> },
    { key: "endpoint", label: "Endpoint", value: (e) => parseKey(e.key).path,
      render: (e) => {
        const { method, path } = parseKey(e.key);
        return (
          <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            <span className="font-medium" style={{ color: "var(--txt-3)" }}>{method}</span> {path}
          </span>
        );
      } },
    { key: "count", label: "Count", sort: true, align: "right", width: "110px", value: (e) => e.count ?? 0,
      render: (e) => (
        <span className="tabular-nums" style={{ color: "var(--txt)" }}>{e.count} / {e.limit}</span>
      ) },
    { key: "status", label: "Status", width: "110px",
      render: (e) => statusBadge(e.count ?? 0, e.limit ?? 0) },
    { key: "resetInSeconds", label: "Resets in", align: "right", width: "100px", sort: true, value: (e) => e.resetInSeconds ?? 0,
      render: (e) => <span className="tabular-nums text-[12px]" style={{ color: "var(--txt-2)" }}>{e.resetInSeconds}s</span> },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            Rate limit
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            Who/what is currently inside a rate-limit window. In-memory snapshot — only reflects
            activity since this instance last restarted.
          </p>
        </div>
        <Button icon="refresh" size="sm" variant="ghost" onClick={fetchSnapshot} loading={loading}>Refresh</Button>
      </header>

      <AdminTable
        columns={columns}
        rows={entries}
        rowKey="key"
        loading={loading}
        emptyIcon="gauge"
        emptyMessage="No active rate-limit windows right now."
        defaultSort={{ key: "count", dir: "desc" }}
      />
    </div>
  );
}
